import { ChangeDetectionStrategy, Component, ChangeDetectorRef, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { UrlBuilder } from '../../../../core';
import { FormControl } from '@angular/forms';
import { Observable, merge, of, Subscription } from 'rxjs';
import { GridDetails, GridHeader, ActionType } from '../../../../sample-tracking/tracking-overview/interfaces/grid';
import { ActivityLogger } from '../../../../activities/activity-logger';
import { DateRangeFilterValue, RangeKind } from '../../../../shared/time-frame-filter/timeframe-filter.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ShipmentFormStorage } from '../../shipment-form-storage';
import { ShipmentFormService } from '../../service/shipment-form-service';
import * as moment from 'moment';
import { ContainerGridData } from '../../resume-shipment/resume-shipment-info';
import { SiteShippingBarcodeDetailsData } from '../../service/container-barcode-info';
import { container } from '@angular/core/src/render3';
import { uniqBy } from 'lodash';

@Component({
    selector: 'shipment-registered-samples',
    template: require('./registered-samples.component.html'),
    styles: [require('./registered-samples.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShipmentRegisteredSamplesComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    public containersInShipment: string[];

    @Input()
    public collapseSamples = false;

    @Output()
    public onAddToShipmentEvent: EventEmitter<any> = new EventEmitter();
    public showExportMenu = false;
    public samplesList: SiteShippingBarcodeDetailsData[] = [];
    public filteredData: SiteShippingBarcodeDetailsData[] = [];
    public gridDetails: GridDetails;
    public isLoadingCompleted = false;
    public defaultSortField = 'dateCollected';
    public searchTerm = new FormControl('');
    public showSubmitAlert: Observable<boolean>;
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: true },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    public searchSubscription: Subscription;
    public selectedDateRange: DateRangeFilterValue;
    public selectedRows: SiteShippingBarcodeDetailsData[] = [];

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private storage: ShipmentFormStorage,
        private shipmentService: ShipmentFormService,
        private activityLogger: ActivityLogger,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        this.searchSubscription = searchTermFlow.subscribe((term) => {
            this.applySearch(term);
            this.changeDetectorRef.markForCheck();
        });
    }

    public ngOnInit() {
        this.loadRegisteredSamplesList();
    }

    public ngOnChanges(simpleChange: SimpleChanges) {
        if (simpleChange.containersInShipment && this.samplesList && this.samplesList.length) {
            this.filterSamplesOnBarcode(this.containersInShipment, this.samplesList);
        }
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
    }

    public registerSample() {
        this.urlBuilder.esa().sampleManagement().registerNewSample().navigate();
    }

    public onGridItemClick(event: any) {

    }

    public addSamplestoShipment() {
        this.showExportMenu = false;
        this.filterSamplesOnBarcode(this.selectedRows.map(t => t.userEnteredBarcode), this.filteredData);
        this.onAddToShipmentEvent.emit(this.selectedRows);
        this.samplesList.forEach(t => t.rowSelected = false);
        this.selectedRows = [];
    }

    public onRowsSelected(event: any[]) {
        let selectedSamples: SiteShippingBarcodeDetailsData[] = event.filter(t => t.rowSelected);
        this.selectedRows = uniqBy([...this.selectedRows, ...selectedSamples], 'registeredContainerId');
    }

    public applySearch(term: string) {
        this.isLoadingCompleted = false;
        if (!term || !term.length || !this.samplesList.length) {
            this.gridDetails = { header: this.getSampleHeader(), data: this.filteredData };
        } else {
            let filterData = this.shipmentService.applySamplesSearch(term, this.getSampleHeader(), this.filteredData);
            this.gridDetails = { header: this.getSampleHeader(), data: filterData };
        }
        this.isLoadingCompleted = true;
        this.changeDetectorRef.markForCheck();
    }

    public loadRegisteredSamplesList() {
        this.isLoadingCompleted = false;
        this.storage.getRegisteredSamplesList().subscribe((result: ContainerGridData[]) => {
            this.formatSamplesList(result);
            this.gridDetails = { header: this.getSampleHeader(), data: this.filteredData };
            this.isLoadingCompleted = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    private filterSamplesOnBarcode(containers: string[], dataList: SiteShippingBarcodeDetailsData[]) {
        if (!containers || !containers.length) {
            this.gridDetails = { header: this.getSampleHeader(), data: this.samplesList };
        } else {
            this.filteredData = dataList.filter(t => containers.indexOf(t.userEnteredBarcode) === -1);
            this.gridDetails = { header: this.getSampleHeader(), data: this.filteredData };
        }
        this.changeDetectorRef.markForCheck();
    }

    private getSampleHeader(): GridHeader[] {
        let headerList = [
            {
                displayText: '',
                key: '',
                defaultValue: ' ',
                selectId: 'registeredContainerId',
                selectRow: true
            },
            {
                key: 'subjectId',
                displayText: 'Subject ID',
                defaultValue: ' ',
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'investigatorSiteNameNumber',
                displayText: 'Investigator Number/Name',
                defaultValue: ' ',
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'visitDescription',
                defaultValue: ' ',
                displayText: 'Visit',
                enableFilter: true,
                enableSearch: true,
                sort: 'desc',
            },
            {
                key: 'containerIdentifier',
                displayText: 'Sample',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
                showIndicator: 'showIndicator',
                indicatorField: 'indicator',
                cssClasses: [
                    {
                        key: 'Alt. Container',
                        class: 'alternateContainer'
                    }
                ]
            },
            {
                key: '',
                displayText: 'Container Label',
                subFields: [{ key: 'containerLabel1'}, { key: 'containerLabel2' }],
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'expectedReturnCondition',
                displayText: 'Expected Return Condition',
                defaultValue: ' ',
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'collectionStatus',
                displayText: 'Status',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'protocolCode',
                displayText: 'Protocol',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },

            {
                key: 'accessionNumber',
                displayText: 'Accession',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'collectionDateTime',
                displayText: 'Date Collected',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
                isDateTime: true,
            }
        ];
        return headerList.map(t => t);
    }

    private formatSamplesList(data: SiteShippingBarcodeDetailsData[]) {
        if (!data || !data.length) { return; }
        let formatedData = data.map(t => {
            let barcode = t.barcodeNumber && t.barcodeNumber.length ? t.barcodeNumber : t.containerIdentifier;
            t.barcodeNumber = barcode;
            t.userEnteredBarcode = t.barcodeRaw = barcode;
            t.collectionDateTime = moment(t.collectionDateTime).isValid ? moment(t.collectionDateTime).format('DD-MMM-YYYY HH:mm') : '';
            if (t.isAlternateContainer) {
                let alternateData: SiteShippingBarcodeDetailsData = {
                    indicator: 'Alt. Container',
                    showIndicator: true,
                    containerIdentifier: t.alternateContainerDescription
                };
                t.children = [alternateData];
            }
            if (t.isAdditionalContainer) {
                t.containerIdentifier = barcode;
                t.barcodeNumber = barcode;
                t.showIndicator = true;
                t.indicator = 'Additional';
            }
            return t; 
        });
        this.samplesList = formatedData.filter(t => !t.isInShipment);
        this.filteredData = this.samplesList.map(t => t);
        if (this.containersInShipment) {
            this.filteredData = this.filteredData.filter(t => this.containersInShipment.indexOf(t.userEnteredBarcode) === -1);
        }
    }
}
